/**
 * Corrige les coquilles légères sur les 3 tags Hector whitelistés
 * (hector-link, hector-quote, hector-callout) avant le parsing aval.
 *
 * Couvre deux familles de défauts observés en prod (un LLM qui « tape à côté ») :
 *  1. Délimiteur de fermeture malformé : `<\hector-callout>` (anti-slash au lieu
 *     de slash). On capture aussi le slash canonique pour la symétrie.
 *  2. Typo dans le NOM du tag à distance d'édition ≤ 1 : `hector-callot`
 *     (u manquant), `hector-calout`, `hectorcallout` (tiret oublié), casse
 *     (`Hector-Callout`)…
 *
 * Pourquoi distance ≤ 1 et pas plus : « la plupart des erreurs sont de toutes
 * petites typos » = exactement la définition d'une distance 1. Les 3 cibles
 * sont assez distinctes pour qu'un seuil de 1 ne produise aucun faux positif —
 * un vrai tag custom (`<hector-custom>`) ou un alias déprécié (`<hector-info>`)
 * reste à distance ≥ 3 et traverse donc INTACT. C'est le compromis qui préserve
 * la garantie « conservative » du package tout en récupérant les coquilles.
 *
 * Portée volontairement bornée (hors scope, par sécurité) :
 *  - Pas de correction des tags de fermeture SANS délimiteur (`<hector-callout>`
 *    réutilisé comme fermeture) : ambigu avec une ouverture, trop risqué.
 *  - Pas de transpositions du préfixe (`hetcor-…`) : distance Levenshtein ≥ 2.
 *    Seule exception : les préfixes déformés observés en prod devant un
 *    suffixe canonique (`hektor-link`, `hepler-callout`…) — voir
 *    MANGLED_PREFIX_RE.
 *
 * Idempotence : la sortie est toujours canonique (distance 0) → un 2ᵉ passage
 * ne déclenche aucune réécriture. Doit tourner sur la PROSE uniquement (le
 * découpage fence-safe est assuré par splitFences en amont).
 *
 * Ordre : à placer AVANT hallucinatedAlias — un `<hector-calout>` redevient
 * `<hector-callout>` officiel avant que les passes suivantes ne s'appliquent.
 */
const CANONICAL = ['hector-link', 'hector-quote', 'hector-callout'] as const;

// Capture large : tout ce qui ressemble à une tentative de tag Hector.
//  g1 = délimiteur de fermeture éventuel (`/` canonique ou `\` malformé)
//  g2 = nom du tag (hector-xxx, tiret optionnel, insensible à la casse)
//  g3 = attributs bruts (préservés verbatim ; normalisés plus tard par whitespaceAttrs)
//  g4 = marqueur self-closing éventuel
const FUZZY_TAG_RE = /<([\\/]?)\s*(hector-?[a-z]+|h[a-z]{2,5}-(?:link|quote|callout))((?:\s[^>]*?)?)(\s*\/?)>/gi;

// Le modèle déforme le PRÉFIXE « hector » mais garde le suffixe : en prod sur
// 7 jours au 26/10/2026, ~1 560 balises sur ~103 000 s'affichaient en texte
// brut (hektor-link 1 255, hepler-link 193, hektor-callout 49, hecker-link 20,
// heler-link 11, hektor-quote 8, hecler-link 7…). Ces variantes sont à
// distance ≥ 2 du canonique, hors du seuil Levenshtein. Liste FERMÉE des
// préfixes observés, et non tout `h…-link` : un `<html-link url=…/>` ou
// `<hyper-link>` recopié d'une pièce doit rester du texte, pas devenir une
// puce cliquable. Un nouveau préfixe s'ajoute ici
// après mesure en base, avec son test.
const MANGLED_PREFIX_RE = /^(?:hektor|hepler|hecker|hecler|heler|helder|heitor|hecor)-(link|quote|callout)$/;

/**
 * Distance de Levenshtein bornée — on ne s'intéresse qu'au seuil 1, mais le DP
 * complet sur des chaînes de ~12 caractères coûte une poignée de microsecondes.
 */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > 1) return 2; // court-circuit : distance > 1 garantie
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  let curr = new Array<number>(n + 1).fill(0);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j]! + 1, curr[j - 1]! + 1, prev[j - 1]! + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n]!;
}

/**
 * Résout un nom capturé vers son tag canonique : retourne le canonique si le
 * nom est exact OU à distance ≤ 1, sinon null (laissé intact).
 */
function resolveCanonical(rawName: string): string | null {
  const name = rawName.toLowerCase();
  for (const c of CANONICAL) {
    if (name === c) return c;
  }
  const mangled = MANGLED_PREFIX_RE.exec(name);
  if (mangled) return `hector-${mangled[1]}`;
  let best: string | null = null;
  let bestDist = 2; // strictement < 2 ⇒ distance ≤ 1
  for (const c of CANONICAL) {
    const d = levenshtein(name, c);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return bestDist <= 1 ? best : null;
}

export function typoTolerance(prose: string): string {
  if (!prose) return prose;

  return prose.replace(
    FUZZY_TAG_RE,
    (whole, delim: string, rawName: string, attrs: string, selfClose: string) => {
      const canonical = resolveCanonical(rawName);
      if (!canonical) return whole; // inconnu ou trop loin → conservative

      const isClosing = delim === '/' || delim === '\\';
      const isSelfClose = selfClose.includes('/');
      // Comparaison sensible à la casse : `canonical` est toujours minuscule,
      // donc un `Hector-Callout` (casse) compte comme un écart à corriger,
      // au même titre qu'une vraie coquille.
      const nameNeedsFix = rawName !== canonical;
      const delimWasMalformed = delim === '\\';

      // Tag déjà parfait → ne rien réécrire (idempotence + conservative).
      if (!nameNeedsFix && !delimWasMalformed) return whole;

      // Fermeture : les tags canoniques de fermeture ne portent jamais d'attrs.
      if (isClosing) return `</${canonical}>`;

      // Ouverture self-closing (hector-link).
      if (isSelfClose) return `<${canonical}${attrs} />`;

      // Ouverture normale (hector-callout, hector-quote).
      return `<${canonical}${attrs}>`;
    }
  );
}
